import { useState, useEffect, useRef } from 'react';
import { companyAPI, stripeAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface AdminPermissions {
  can_manage_plan: boolean;
  can_view_financials: boolean;
  can_manage_admins: boolean;
  // Main nav
  access_customers: boolean;
  access_products: boolean;
  access_orders: boolean;
  // Warehouse
  access_ingredients: boolean;
  access_suppliers: boolean;
  access_inventory: boolean;
  access_purchase_orders: boolean;
  // Kitchen
  access_recipes: boolean;
  access_starters: boolean;
  access_production: boolean;
  // Tools
  access_batch_optimizer: boolean;
  access_cost_calculator: boolean;
  access_baker_calculator: boolean;
  // Webstore
  access_store_settings: boolean;
  access_store_customers: boolean;
  access_store_orders: boolean;
  access_messages: boolean;
  // Other
  access_support: boolean;
}

const DEFAULT_PERMISSIONS: AdminPermissions = {
  can_manage_plan: false,
  can_view_financials: true,
  can_manage_admins: false,
  access_customers: false,
  access_products: false,
  access_orders: false,
  access_ingredients: false,
  access_suppliers: false,
  access_inventory: false,
  access_purchase_orders: false,
  access_recipes: false,
  access_starters: false,
  access_production: false,
  access_batch_optimizer: false,
  access_cost_calculator: false,
  access_baker_calculator: false,
  access_store_settings: false,
  access_store_customers: false,
  access_store_orders: false,
  access_messages: false,
  access_support: false,
};

export interface SubAdmin {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  is_active: boolean;
  created_at: string;
  permissions: AdminPermissions;
}

export function useCompanySettings() {
  const { user, isExpired } = useAuth();
  const isOwner = user?.is_company_owner !== false;
  const isSubAdmin = user?.is_company_admin && !user?.is_company_owner;
  const canManageAdmins = isOwner || (isSubAdmin && user?.permissions?.can_manage_admins === true);
  const canManagePlan = isOwner || (isSubAdmin && user?.permissions?.can_manage_plan === true);

  // Logo state
  const [logoInfo, setLogoInfo] = useState<{ logoUrl: string | null; hasCustomBranding: boolean }>({
    logoUrl: null, hasCustomBranding: false,
  });
  const { logoUrl, hasCustomBranding } = logoInfo;
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Subscription state
  const [subStatus, setSubStatus] = useState<any>(null);
  const [subLoading, setSubLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  // Sub-admin state
  const [admins, setAdmins] = useState<SubAdmin[]>([]);
  const [maxAdmins, setMaxAdmins] = useState(1);
  const [canAddAdmin, setCanAddAdmin] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<SubAdmin | null>(null);
  const [adminForm, setAdminForm] = useState({ username: '', email: '', first_name: '', last_name: '', phone_number: '', password: '', permissions: { ...DEFAULT_PERMISSIONS } as AdminPermissions });
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminMessage, setAdminMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  // Order settings state
  const [orderSettings, setOrderSettings] = useState<{ leadDays: number; companyTimezone: string }>({
    leadDays: 2, companyTimezone: 'America/Chicago',
  });
  const { leadDays, companyTimezone } = orderSettings;
  const [leadDaysLoading, setLeadDaysLoading] = useState(true);
  const [leadDaysSaving, setLeadDaysSaving] = useState(false);
  const [leadDaysMessage, setLeadDaysMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  // Timezone state
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [timezoneMessage, setTimezoneMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://127.0.0.1:8001'
    : '';

  useEffect(() => {
    companyAPI.getLogo()
      .then(res => {
        const url = res.data.logo_url
          ? (res.data.logo_url.startsWith('http') ? res.data.logo_url : apiBase + res.data.logo_url)
          : null;
        setLogoInfo({ logoUrl: url, hasCustomBranding: res.data.has_custom_branding || false });
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    stripeAPI.getSubscriptionStatus()
      .then(res => setSubStatus(res.data))
      .catch(() => {})
      .finally(() => setSubLoading(false));

    if (canManageAdmins) {
      fetchAdmins();
    } else {
      setAdminsLoading(false);
    }

    companyAPI.getOrderSettings()
      .then(res => {
        setOrderSettings({
          leadDays: res.data.min_delivery_lead_days ?? 2,
          companyTimezone: res.data.company_timezone || 'America/Chicago',
        });
      })
      .catch(() => {})
      .finally(() => setLeadDaysLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const fetchAdmins = () => {
    setAdminsLoading(true);
    companyAPI.getAdmins()
      .then(res => {
        setAdmins(res.data.admins || []);
        setMaxAdmins(res.data.max_admins || 1);
        setCanAddAdmin(res.data.can_add_admin || false);
      })
      .catch(() => {})
      .finally(() => setAdminsLoading(false));
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await stripeAPI.createCustomerPortal();
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (err: any) {
      setMessage({ type: 'danger', text: err?.response?.data?.error || 'Failed to open subscription portal' });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'danger', text: 'Invalid file type. Allowed: JPEG, PNG, GIF, WebP, SVG' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'danger', text: 'File too large. Maximum size is 2MB.' });
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMessage(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setMessage(null);
    try {
      const res = await companyAPI.uploadLogo(selectedFile);
      const url = res.data.logo_url;
      setLogoInfo(prev => ({ ...prev, logoUrl: url.startsWith('http') ? url : apiBase + url }));
      setPreviewUrl(null);
      setSelectedFile(null);
      setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
    } catch (err: any) {
      const errMsg = err.response?.data?.error || 'Failed to upload logo';
      setMessage({ type: 'danger', text: errMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove your company logo?')) return;
    setUploading(true);
    setMessage(null);
    try {
      await companyAPI.deleteLogo();
      setLogoInfo(prev => ({ ...prev, logoUrl: null }));
      setPreviewUrl(null);
      setSelectedFile(null);
      setMessage({ type: 'success', text: 'Logo removed.' });
    } catch {
      setMessage({ type: 'danger', text: 'Failed to remove logo.' });
    } finally {
      setUploading(false);
    }
  };

  // Sub-admin handlers
  const openAddAdminModal = () => {
    setEditingAdmin(null);
    setAdminForm({ username: '', email: '', first_name: '', last_name: '', phone_number: '', password: '', permissions: { ...DEFAULT_PERMISSIONS } });
    setAdminMessage(null);
    setShowAdminModal(true);
  };

  const openEditAdminModal = (admin: SubAdmin) => {
    setEditingAdmin(admin);
    setAdminForm({
      username: admin.username,
      email: admin.email,
      first_name: admin.first_name,
      last_name: admin.last_name,
      phone_number: admin.phone_number || '',
      password: '',
      permissions: admin.permissions || { ...DEFAULT_PERMISSIONS },
    });
    setAdminMessage(null);
    setShowAdminModal(true);
  };

  const handleSaveAdmin = async () => {
    setAdminSaving(true);
    setAdminMessage(null);
    try {
      if (editingAdmin) {
        const data: any = { ...adminForm };
        if (!data.password) delete data.password;
        await companyAPI.updateAdmin(editingAdmin.id, data);
        setAdminMessage({ type: 'success', text: 'Admin updated successfully' });
      } else {
        if (!adminForm.password) {
          setAdminMessage({ type: 'danger', text: 'Password is required for new admins' });
          setAdminSaving(false);
          return;
        }
        await companyAPI.createAdmin(adminForm);
        setAdminMessage({ type: 'success', text: 'Admin created successfully' });
      }
      fetchAdmins();
      setTimeout(() => setShowAdminModal(false), 800);
    } catch (err: any) {
      const errData = err.response?.data;
      let errMsg = 'Failed to save admin';
      if (typeof errData === 'object' && errData) {
        if (errData.error) errMsg = errData.error;
        else if (errData.username) errMsg = `Username: ${errData.username}`;
        else if (errData.email) errMsg = `Email: ${errData.email}`;
      }
      setAdminMessage({ type: 'danger', text: errMsg });
    } finally {
      setAdminSaving(false);
    }
  };

  const handleDeleteAdmin = async (admin: SubAdmin) => {
    if (!window.confirm(`Remove admin "${admin.username}"? This cannot be undone.`)) return;
    try {
      await companyAPI.deleteAdmin(admin.id);
      fetchAdmins();
    } catch {
      setAdminMessage({ type: 'danger', text: 'Failed to remove admin' });
    }
  };

  const handleToggleActive = async (admin: SubAdmin) => {
    try {
      await companyAPI.updateAdmin(admin.id, { is_active: !admin.is_active });
      fetchAdmins();
    } catch {
      // ignore
    }
  };

  const displayUrl = previewUrl || (hasCustomBranding ? logoUrl : null);

  const handleSaveLeadDays = async (value: number) => {
    setLeadDaysSaving(true);
    setLeadDaysMessage(null);
    try {
      await companyAPI.updateOrderSettings({ min_delivery_lead_days: value });
      setOrderSettings(prev => ({ ...prev, leadDays: value }));
      setLeadDaysMessage({ type: 'success', text: 'Delivery lead time updated' });
      setTimeout(() => setLeadDaysMessage(null), 3000);
    } catch (err: any) {
      setLeadDaysMessage({ type: 'danger', text: err?.response?.data?.error || 'Failed to update' });
    } finally {
      setLeadDaysSaving(false);
    }
  };

  const handleSaveTimezone = async (tz: string) => {
    setTimezoneSaving(true);
    setTimezoneMessage(null);
    try {
      await companyAPI.updateOrderSettings({ company_timezone: tz });
      setOrderSettings(prev => ({ ...prev, companyTimezone: tz }));
      setTimezoneMessage({ type: 'success', text: 'Timezone updated' });
      setTimeout(() => setTimezoneMessage(null), 3000);
    } catch (err: any) {
      setTimezoneMessage({ type: 'danger', text: err?.response?.data?.error || 'Failed to update timezone' });
    } finally {
      setTimezoneSaving(false);
    }
  };

  // Helper: subscription tier color/label
  const isPaid = subStatus?.subscription_tier === 'paid';
  const cancelAtPeriodEnd = subStatus?.cancel_at_period_end === true;
  const cancelsAt = subStatus?.cancels_at ? new Date(subStatus.cancels_at) : null;
  const tierColor = cancelAtPeriodEnd ? '#d97706' : isPaid ? '#059669' : '#d97706';
  const tierLabel = cancelAtPeriodEnd ? 'Cancelling' : isPaid ? 'Active' : 'Free Trial';
  const trialPercentage = subStatus?.days_remaining != null && !isPaid
    ? Math.max(0, Math.min(100, (subStatus.days_remaining / 14) * 100))
    : null;

  return {
    // Auth
    user, isExpired, isOwner, canManageAdmins, canManagePlan,
    // Logo
    logoUrl, hasCustomBranding, loading, uploading, message, setMessage, previewUrl, selectedFile, fileInputRef, displayUrl,
    handleFileSelect, handleUpload, handleDelete,
    // Subscription
    subStatus, subLoading, portalLoading, handleManageSubscription,
    isPaid, tierColor, tierLabel, trialPercentage, cancelAtPeriodEnd, cancelsAt,
    // Admin
    admins, maxAdmins, canAddAdmin, adminsLoading, showAdminModal, setShowAdminModal, editingAdmin, adminForm, setAdminForm,
    showAdminPassword, setShowAdminPassword, adminSaving, adminMessage, setAdminMessage,
    openAddAdminModal, openEditAdminModal, handleSaveAdmin, handleDeleteAdmin, handleToggleActive,
    // Order Settings
    leadDays, companyTimezone, leadDaysLoading, leadDaysSaving, leadDaysMessage, setLeadDaysMessage,
    handleSaveLeadDays,
    // Timezone
    timezoneSaving, timezoneMessage, setTimezoneMessage, handleSaveTimezone,
  };
}
